import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Typy ról
export type UserRole = 'admin' | 'sales' | 'quality' | 'production' | 'viewer';

// Uprawnienia dla modułów
export interface ModulePermissions {
  offers: boolean;
  production: boolean;
  clients: boolean;
  products: boolean;
  calculations: boolean;
  payroll: boolean;
  settings: boolean;
  reports: boolean;
  automation: boolean;
  knowledgeBase: boolean;
  marketplace: boolean;
}

// Mapa uprawnień dla każdej roli
const ROLE_PERMISSIONS: Record<UserRole, ModulePermissions> = {
  admin: {
    offers: true,
    production: true,
    clients: true,
    products: true,
    calculations: true,
    payroll: true,
    settings: true,
    reports: true,
    automation: true,
    knowledgeBase: true,
    marketplace: true
  },
  sales: {
    offers: true,
    production: false,
    clients: true,
    products: true,
    calculations: true,
    payroll: false,
    settings: false,
    reports: true,
    automation: false,
    knowledgeBase: true,
    marketplace: true
  },
  quality: {
    offers: false,
    production: true,
    clients: false,
    products: true,
    calculations: false,
    payroll: false,
    settings: false,
    reports: true,
    automation: false,
    knowledgeBase: true,
    marketplace: false
  },
  production: {
    offers: false,
    production: true,
    clients: false,
    products: true,
    calculations: true,
    payroll: false,
    settings: false,
    reports: false,
    automation: false,
    knowledgeBase: true,
    marketplace: false
  },
  viewer: {
    offers: true,
    production: true,
    clients: true,
    products: true,
    calculations: false,
    payroll: false,
    settings: false,
    reports: true,
    automation: false,
    knowledgeBase: true,
    marketplace: false
  }
};

interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean;
  isAdmin: boolean;
  role: UserRole | null;
  permissions: ModulePermissions | null;
  setUserRole: (role: UserRole) => void;
  hasPermission: (module: keyof ModulePermissions) => boolean;
  signOut: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<ModulePermissions | null>(null);
  const [loading] = useState(false);

  // Sprawdź czy użytkownik ma zapisaną rolę w localStorage
  useEffect(() => {
    const savedRole = localStorage.getItem('userRole') as UserRole | null;
    if (savedRole && ROLE_PERMISSIONS[savedRole]) {
      setUserRole(savedRole);
    }
  }, []);

  const setUserRole = (newRole: UserRole) => {
    setRole(newRole);
    setPermissions(ROLE_PERMISSIONS[newRole]);
    localStorage.setItem('userRole', newRole);
    
    // Ustaw dane użytkownika na podstawie roli
    const roleNames: Record<UserRole, string> = {
      admin: 'Administrator',
      sales: 'Handlowiec',
      quality: 'Kontrola Jakości',
      production: 'Produkcja',
      viewer: 'Podgląd'
    };

    setUser({
      id: newRole,
      email: `${newRole}@plexisystem.pl`,
      full_name: roleNames[newRole],
      role: newRole
    });
  };

  const hasPermission = (module: keyof ModulePermissions): boolean => {
    if (!permissions) return false;
    return permissions[module] || false;
  };

  const signOut = async () => {
    setUser(null);
    setRole(null);
    setPermissions(null);
    localStorage.removeItem('userRole');
  };

  const value = {
    user,
    setUser,
    loading,
    isAdmin: role === 'admin',
    role,
    permissions,
    setUserRole,
    hasPermission,
    signOut
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
